import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  GridLegacy as Grid,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { commandCopy } from "components/commandPalette/commandBuilders";
import { useRegisterCommands } from "components/CommandPaletteRegistry";
import { Dialog } from "components/Dialog";
import { FormAutocomplete } from "components/Forms/FormAutocomplete";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { ToggleField } from "components/Forms/ToggleField";
import { FormTextFieldSkeleton } from "components/Skeleton";
import { safeParseInt } from "encamp-shared/src/tierii/utilities";
import {
  ChemicalDbRequirement,
  PortalInput,
  PortalType,
  ProgramArea,
} from "generated-graphql/graphql";
import { useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { GET_PORTAL, PORTAL_SEARCH, UPSERT_PORTAL } from "./api";

type PortalDialogProps = {
  onClose: () => void;
  mode?: "add" | "edit";
  portalId?: string;
};

const PORTAL_TYPE_OPTIONS = [
  { value: PortalType.State, display: "State" },
  { value: PortalType.Lepc, display: "LEPC" },
  { value: PortalType.City, display: "City" },
  { value: PortalType.Federal, display: "Federal" },
];

const CHEMICAL_DB_OPTIONS = [
  { value: ChemicalDbRequirement.No, display: "No" },
  { value: ChemicalDbRequirement.EhsOnly, display: "EHS Only" },
  { value: ChemicalDbRequirement.All, display: "All" },
];

const PROGRAM_AREA_OPTIONS = [
  { value: ProgramArea.Epcra, display: "EPCRA" },
  { value: ProgramArea.Rcra, display: "RCRA" },
];

/** Suggested portal tech options; freeSolo allows any custom value. */
const PORTAL_TECH_OPTIONS = [
  { value: "EPlan", label: "ePlan" },
  { value: "Tier2Manager", label: "TierIIManager" },
];

/** Min height to match FormControl (label + outlined input + FormHelperText) so skeleton doesn't jump when form loads. */
const FORM_FIELD_MIN_HEIGHT = 96;

function PortalFormSkeleton() {
  const theme = useTheme();
  return (
    <Box
      component="form"
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Grid container spacing={3}>
        {/* Description (8) and Jurisdiction (4) - match first row */}
        <Grid item xs={12} md={8}>
          <Box sx={{ minHeight: 100 }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        {/* Type (6) and Portal Tech (6) */}
        <Grid item xs={12} md={6}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        {/* URL (6) and Capacity (6) */}
        <Grid item xs={12} md={6}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        {/* Chemical DB (6) and Program Areas (6) */}
        <Grid item xs={12} md={6}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ minHeight: FORM_FIELD_MIN_HEIGHT }}>
            <FormTextFieldSkeleton />
          </Box>
        </Grid>
        {/* Login options - match Typography variant="subtitle2" + mb: 1 */}
        <Grid item xs={12}>
          <Skeleton
            variant="text"
            width={100}
            height={20}
            sx={{ mb: theme.spacing(1) }}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} md={6} key={i}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Skeleton variant="rounded" width={44} height={24} />
                  <Skeleton variant="text" width={120} height={24} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export const CreateEditPortalDialog: React.FC<PortalDialogProps> = ({
  onClose,
  mode,
  portalId,
}) => {
  const alerts = useAlerts();

  const { data: portalData, loading: isLoadingPortal } = useQuery(GET_PORTAL, {
    fetchPolicy: "cache-and-network",
    variables: { id: portalId ?? "" },
    skip: !portalId,
  });

  const [upsertPortal, { loading: isUpserting }] = useMutation(UPSERT_PORTAL, {
    refetchQueries: ["PortalSearch"],
  });

  const portal = portalData?.portal;

  const defaultValues: PortalInput = useMemo(
    () => ({
      type: portal?.type ?? PortalType.State,
      jurisdiction: portal?.jurisdiction ?? "",
      portalTech: portal?.portalTech ?? "",
      url: portal?.url ?? null,
      capacity: portal?.capacity ?? 10,
      allowsMultiSessionLogin: portal?.allowsMultiSessionLogin ?? false,
      allowsCookieReuse: portal?.allowsCookieReuse ?? false,
      requiresUsageOfChemicalDb:
        portal?.requiresUsageOfChemicalDb ?? ChemicalDbRequirement.No,
      usesMFA: portal?.usesMFA ?? false,
      supportedProgramAreas: portal?.supportedProgramAreas ?? [
        ProgramArea.Epcra,
      ],
      description: portal?.description ?? null,
      isActive: portal?.isActive ?? true,
      verifiable: portal?.verifiable ?? true,
    }),
    [portal]
  );

  const form = useForm<PortalInput>({
    defaultValues,
    values: portal ? defaultValues : undefined,
  });

  const { control, getValues, formState } = form;

  const onSubmit = async () => {
    const input = getValues();
    const capacity =
      typeof input.capacity === "number"
        ? input.capacity
        : safeParseInt(String(input.capacity)) ?? 10;
    await upsertPortal({
      variables: {
        id: mode === "edit" ? portalId : null,
        input: {
          ...input,
          capacity,
          jurisdiction:
            mode === "edit"
              ? portal?.jurisdiction ?? input.jurisdiction
              : input.jurisdiction,
          portalTech: input.portalTech || null,
        },
      },
      onCompleted: () => {
        onClose();
        alerts.success("Portal saved successfully");
      },
      onError: (error) => {
        alerts.error(
          error.message ?? "An error occurred while saving the portal"
        );
      },
    });
  };

  const savingOrLoading = isLoadingPortal || isUpserting;
  const showEditSkeleton = mode === "edit" && isLoadingPortal && !portal;

  const copyToClipboardWithAlert = useCallback(
    async (text: string) => {
      await navigator.clipboard.writeText(text);
      alerts.success("Copied to clipboard");
    },
    [alerts]
  );

  const portalCopyCommands = useMemo(() => {
    if (mode !== "edit" || !portalId) return [];
    return [
      commandCopy({
        id: "portal-dialog-copy-portal-id",
        idKind: "portal ID",
        value: portalId,
        copy: copyToClipboardWithAlert,
        signature: `portal-id:${portalId}`,
        keywords: ["portal", "id", "copy"],
      }),
    ];
  }, [mode, portalId, copyToClipboardWithAlert]);

  useRegisterCommands(
    portalCopyCommands,
    [portalCopyCommands],
    "portal-dialog"
  );

  return (
    <FormProvider {...form}>
      <Dialog
        open
        onClose={onClose}
        maxWidth="md"
        title={`${mode === "add" ? "Add" : "Edit"} Portal`}
        titleBarActionElement={
          showEditSkeleton ? undefined : (
            <Box sx={{ display: "flex", alignItems: "center", marginTop: 3 }}>
              <ToggleField
                name="isActive"
                label="Active"
                control={control}
                sx={{ margin: 0 }}
              />
            </Box>
          )
        }
        primaryActionButtonProps={{
          loading: savingOrLoading,
          onClick: onSubmit,
          disabled: !formState.isDirty,
        }}
      >
        {showEditSkeleton ? (
          <PortalFormSkeleton />
        ) : (
          <Box
            component="form"
            sx={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              width: "100%",
            }}
          >
            <Grid container spacing={3}>
              {/* Description (8) and Jurisdiction (4) at top */}
              <Grid item xs={12} md={8}>
                <FormTextField
                  name="description"
                  label="Description"
                  control={control}
                  textFieldProps={{ multiline: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormTextField
                  name="jurisdiction"
                  label="Jurisdiction"
                  control={control}
                  tooltip="Use state abbreviation or abbreviation_LEPCID"
                  disabled={mode === "edit"}
                />
              </Grid>

              {/* Type (6) and Portal Tech (6) */}
              <Grid item xs={12} md={6}>
                <FormSelect
                  name="type"
                  label="Type"
                  control={control}
                  selectItems={PORTAL_TYPE_OPTIONS}
                  tooltip="Use State for state-level portals (e.g. CA, TX). Use LEPC for county/local portals (e.g. VA Fairfax, NY NYC)—many LEPC credentials were incorrectly marked as State in the past."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormAutocomplete
                  name="portalTech"
                  label="Portal Tech"
                  control={control}
                  autocompleteItems={PORTAL_TECH_OPTIONS}
                  autocompleteProps={{ freeSolo: true }}
                />
              </Grid>

              {/* URL, Capacity, Chemical DB, Program Areas */}
              <Grid item xs={12} md={6}>
                <FormTextField
                  name="url"
                  label="URL"
                  control={control}
                  textFieldProps={{ type: "url" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormTextField
                  name="capacity"
                  label="Capacity"
                  control={control}
                  textFieldProps={{ type: "number", inputProps: { min: 1 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormSelect
                  name="requiresUsageOfChemicalDb"
                  label="Chemical DB Requirement"
                  control={control}
                  selectItems={CHEMICAL_DB_OPTIONS}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormSelect
                  name="supportedProgramAreas"
                  label="Supported Program Areas"
                  control={control}
                  selectItems={PROGRAM_AREA_OPTIONS}
                  selectProps={{ multiple: true }}
                />
              </Grid>

              {/* Login options */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Login options
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <ToggleField
                      name="allowsMultiSessionLogin"
                      label="Multi-Session Login"
                      tooltip="A single credential may log in multiple times at once"
                      control={control}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ToggleField
                      name="allowsCookieReuse"
                      label="Reuses Cookies"
                      tooltip="Skip logout and then safely refresh session from a saved cookie"
                      control={control}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ToggleField
                      name="usesMFA"
                      label="Multi-Factor Authentication"
                      tooltip="The login requires submitting a code from an email or other source"
                      control={control}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ToggleField
                      name="verifiable"
                      label="Verifiable"
                      tooltip="This portal supports credential verification"
                      control={control}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}
      </Dialog>
    </FormProvider>
  );
};
